// contexts/UserContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import authService from "../services/authService";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import RegistrationPendingModal from "../components/auth/RegistrationPendingModal";

const UserContext = createContext();

const returnPathFromOAuthState = (state) => {
  try {
    const encoded = state.split(".")[1];
    if (!encoded) return null;
    const padded = encoded.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((encoded.length + 3) % 4);
    const path = decodeURIComponent(escape(atob(padded)));
    return path.startsWith("/") && !path.startsWith("//") ? path : null;
  } catch (_) { return null; }
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userProfileLoaded, setUserProfileLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBanned, setIsBanned] = useState(false);
  const [banInfo, setBanInfo] = useState(null);
  const [showPendingRegistration, setShowPendingRegistration] = useState(
    () => new URLSearchParams(window.location.search).get("nw_pending_registration") === "1"
  );
  const [isProcessingOAuth, setIsProcessingOAuth] = useState(
    () => {
      const p = new URLSearchParams(window.location.search);
      return !!(p.get("code") && p.get("state") && sessionStorage.getItem("pkce_code_verifier"));
    }
  );



  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // One-time registration invite link (?invite=TOKEN) — kept for the whole
    // tab session so it survives the Google OAuth redirect round-trip.
    const invite = params.get("invite");
    if (invite) {
      sessionStorage.setItem("nw_invite", invite);
      params.delete("invite");
      const qs = params.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (qs ? `?${qs}` : ""));
    }

    const code = params.get("code");
    const state = params.get("state");

    if (!code || !state || !sessionStorage.getItem("pkce_code_verifier")) return;

    window.history.replaceState({}, document.title, window.location.pathname);

    const returnPath = returnPathFromOAuthState(state) || sessionStorage.getItem("nw_oauth_return_path") || sessionStorage.getItem("google_return_path") || "/";

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await authService.handleGoogleOAuthCallback(code, state);
        if (result.banned) {
          setIsBanned(true);
          setBanInfo(result.banInfo);
          setLoading(false);
          return;
        }
        if (result.success) {
          if (result.user?.registrationStatus === "pending") {
            sessionStorage.removeItem("nw_oauth_return_path");
            const destination = new URL(returnPath, window.location.origin);
            destination.searchParams.set("nw_pending_registration", "1");
            window.location.replace(destination.pathname + destination.search + destination.hash);
            return;
          }
          sessionStorage.removeItem("nw_oauth_return_path");
          window.location.replace(returnPath);
        } else {
          console.error("Google sign-in failed:", result.error);
          // Firebase sign-in may already have succeeded (the failure can come
          // from a later profile/registration step). The URL was stripped to
          // "/" above — go back to where the user started instead of
          // stranding them on the homepage.
          if (returnPath !== "/") {
            window.location.replace(returnPath);
            return;
          }
          setError(result.error || "Google sign-in failed");
          setIsProcessingOAuth(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Google sign-in failed:", err);
        if (returnPath !== "/") {
          window.location.replace(returnPath);
          return;
        }
        setError(err.message || "Google sign-in failed");
        setIsProcessingOAuth(false);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let unsubscribeUserData;
    let unsubscribeAuth;

    const setupAuth = () => {
      unsubscribeAuth = authService.onAuthStateChange(async (firebaseUser) => {
        if (firebaseUser && !firebaseUser.isAnonymous) {
          setUser(firebaseUser);
          setUserProfileLoaded(false);

          const userRef = doc(db, "users", firebaseUser.uid);
          unsubscribeUserData = onSnapshot(
            userRef,
            (snap) => {
              if (snap.exists()) {
                const data = snap.data();
                setUserData(data);
                // Pending status is intentionally re-announced on every fresh
                // page load/sign-in; dismissal only affects this mounted view.
                if (data.registrationStatus === "pending") {
                  setShowPendingRegistration(true);
                }
                if (data.isBanned) {
                  setIsBanned(true);
                  setBanInfo(data.banInfo || { reason: "Your account has been suspended", timestamp: new Date() });
                } else {
                  setIsBanned(false);
                  setBanInfo(null);
                }
              } else {
                // A signed-in session whose users/{uid} doc is confirmed
                // missing by the SERVER is a deleted (or never-registered)
                // account — end the session. Skip cache-only snapshots and
                // the mid-sign-in window where the auth Lambda hasn't
                // written the doc yet.
                if (!snap.metadata.fromCache && !authService.isRegistrationInFlight()) {
                  authService.signOut();
                  return;
                }
                setUserData(null);
              }
              setUserProfileLoaded(true);
            },
            (error) => {
              if (error.code === "permission-denied") {
                setUserData(null);
                setIsBanned(false);
                setBanInfo(null);
                setUserProfileLoaded(true);
              } else {
                console.error("Error listening to user data:", error);
              }
            }
          );
        } else {
          if (unsubscribeUserData) unsubscribeUserData();
          setUser(null);
          setUserData(null);
          setUserProfileLoaded(true);
          setIsBanned(false);
          setBanInfo(null);
        }
        setLoading(false);
      });
    };

    setupAuth();

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeUserData) unsubscribeUserData();
    };
  }, []);

  const signInWithGoogle = async (returnPath) => {
    setError(null);
    try {
      await authService.initiateGoogleRedirect(returnPath);
    } catch (err) {
      setError(err.message || "Could not start Google sign-in");
    }
  };

  const signInWithGoogleToken = async (idToken) => {
    setLoading(true);
    setError(null);
    const result = await authService.signInWithGISCredential(idToken);
    if (result.banned) {
      setIsBanned(true);
      setBanInfo(result.banInfo);
    }
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
    return result;
  };

  const requestEmailOtp = async (email) => {
    setError(null);
    try {
      return await authService.requestEmailOtp(email);
    } catch (err) {
      const message = err.message || "Could not send the sign-in code";
      setError(message);
      throw err;
    }
  };

  const verifyEmailOtp = async (email, requestId, code) => {
    setLoading(true);
    setError(null);
    const result = await authService.verifyEmailOtp(email, requestId, code);
    if (result.banned) {
      setIsBanned(true);
      setBanInfo(result.banInfo);
    }
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
    return result;
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);
    const result = await authService.signOut();
    if (!result.success) {
      setError(result.error);
    }
    setIsBanned(false);
    setBanInfo(null);
    setLoading(false);
    return result;
  };

  const hasPermission = (permission) => {
    if (isBanned) return false;
    if (!userData) return false;
    return userData.permissions?.[permission] || false;
  };

  const isInGroup = (groupName) => {
    if (isBanned) return false;
    if (!userData) return false;
    return userData.groups?.includes(groupName) || false;
  };

  const canViewPrices = () => hasPermission("canViewPrices");
  const isPending = userData?.registrationStatus === "pending";
  const canPurchase = () => !isPending && hasPermission("canPurchase");
  const isAdmin = () => hasPermission("isAdmin");
  const isCorporateOwner = () => hasPermission("isCorporateOwner");

  const checkBanStatus = async () => {
    if (!user) return { isBanned: false };
    if (isBanned) return { isBanned: true, banInfo };
    if (sessionStorage.getItem("userIsBanned") === "true") {
      return {
        isBanned: true,
        banInfo: banInfo || { reason: "Your account has been suspended" },
      };
    }

    try {
      const status = await authService.checkUserBanStatus(user.uid);
      if (status.isBanned !== isBanned) {
        setIsBanned(status.isBanned);
        setBanInfo(status.banInfo);
        if (status.isBanned) {
          sessionStorage.setItem("userIsBanned", "true");
        }
      }
      return status;
    } catch (error) {
      console.error("Error checking ban status:", error);
      return { isBanned: false };
    }
  };

  const value = {
    // Pending identities remain available as authUser for gifts and existing
    // licenses, but look like visitors across the portal and checkout.
    user,
    authUser: user,
    userData,
    userProfileLoaded,
    loading,
    error,
    isProcessingOAuth,
    signInWithGoogle,
    signInWithGoogleToken,
    requestEmailOtp,
    verifyEmailOtp,
    signOut,
    hasPermission,
    isInGroup,
    canViewPrices,
    canPurchase,
    isPendingRegistration: isPending,
    showPendingRegistration: () => setShowPendingRegistration(true),
    isAdmin,
    isCorporateOwner,
    isBanned,
    banInfo,
    checkBanStatus,
  };

  return <UserContext.Provider value={value}>
    {children}
    <RegistrationPendingModal open={showPendingRegistration} onClose={() => {
      const destination = new URL(window.location.href);
      destination.searchParams.delete("nw_pending_registration");
      window.history.replaceState({}, document.title, destination.pathname + destination.search + destination.hash);
      setShowPendingRegistration(false);
    }} />
  </UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserContext;
